import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import React from 'react';
import styled from 'styled-components';

import { useCanViewTranslation, MessagePage } from 'modules/experience-core';
import { ExperienceVenueEntity, PageHeader } from 'modules/experience-core';
import { useExperienceModuleContext } from 'modules/experience-core';
import { useIsVenueLevelMenuEditorEnabled } from 'modules/menu/hooks/useIsVenueLevelMenuEditorEnabled';
import { useIsListingManagerEnabled } from 'modules/shared-menu/hooks/useIsListingManagerEnabled';
import { VenueLevelTranslationsPage } from 'modules/shared-menu/pages/VenueLevelTranslationsPage';

import Translation from './components/Translation';
import { useSharedMenuLink } from './hooks/useSharedMenuLink';

const SpinnerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const TranslationRoot = ({ experienceVenueEntity }: { experienceVenueEntity: ExperienceVenueEntity }) => {
  const venue = experienceVenueEntity.experienceEntityObject;
  const { sharedMenuLink, isSharedMenuLinkLoading } = useSharedMenuLink(venue.id);
  const { isVenueLevelMenuEditorEnabled, isVenueLevelMenuEditorEnabledLoading } = useIsVenueLevelMenuEditorEnabled();
  const { isListingManagerBetaEnabled, isListingManagerEnabledLoading } = useIsListingManagerEnabled(venue);

  if (isSharedMenuLinkLoading || isVenueLevelMenuEditorEnabledLoading || isListingManagerEnabledLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="large" color="blue" />
      </SpinnerContainer>
    );
  }

  const venueIsUsingOfferingPlatform = !!sharedMenuLink?.selection_id;
  const isSingleVenue = venueIsUsingOfferingPlatform && !sharedMenuLink?.selection_is_shared;
  const isSharedVenue = venueIsUsingOfferingPlatform && sharedMenuLink?.selection_is_shared;
  const isDaasVenue = venue.type === 'daas';

  if (isDaasVenue) {
    return <MessagePage variant="not_supported" />;
  }

  const isVlmaEnabled = isVenueLevelMenuEditorEnabled || isListingManagerBetaEnabled;

  if (isSingleVenue && isVlmaEnabled) {
    // Redirect to the new translations page (name is plural - translations)
    return (
      <>
        <PageHeader />
        <VenueLevelTranslationsPage
          venue={venue}
          isSharedVenue={isSharedVenue}
          isSingleVenue={isSingleVenue}
          selectionId={sharedMenuLink?.selection_id}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader />
      <Translation venue={venue} />
    </>
  );
};

export const ExperienceTranslation = () => {
  const { selectedExperienceVenueEntity } = useExperienceModuleContext();
  const { isLoading, hasPermissions: canViewTranslations } = useCanViewTranslation(selectedExperienceVenueEntity);

  if (!selectedExperienceVenueEntity) {
    return <MessagePage variant="error" />;
  }

  if (isLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="large" color="blue" />
      </SpinnerContainer>
    );
  }

  return canViewTranslations ? (
    <TranslationRoot experienceVenueEntity={selectedExperienceVenueEntity} />
  ) : (
    <MessagePage variant="missing_permissions" />
  );
};
